import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { Formik, FormikConfig, FormikHelpers } from 'formik';
import { Button, Form, Select, InputNumber, message } from 'antd';

import FormField from 'components/FormField';
import TransComponent from 'components/TransComponent';
import useMixingTokoText from 'hooks/business/useMixingTokoText';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import {
  scenarioFirstRoundMinTimeMap,
  scenarioTriggerActionI18nMap,
  scenarioTypeI18nLabelMap,
} from './config';
import { T, useTranslate } from '@tolgee/react';

export interface RoundFormValues extends Marketing.Round {}

type FV = RoundFormValues;

export type RoundFormHelpers = FormikHelpers<FV>;

export interface RoundFormProps extends Omit<FormikConfig<FV>, 'onSubmit'> {
  disabled?: boolean;
  scenarioType: Marketing.ScenarioType | 'INVITE_FOLLOWER';
  task: Marketing.Task;
  onRoundUpdate: (round: Marketing.Round) => Promise<any>;
}

export const InlineItem = styled.span`
  display: inline-block;
  padding: 0 8px;
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    width: 90px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item-explain')} {
    white-space: nowrap;
  }
`;

const unitTimeMinMap = {
  '1': 2,
  '2': 1,
};
const unitTimeMaxMap = {
  '1': 1440,
  '2': 24,
};

export const timeEnMap: Record<string | number, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
};

export const getTimes = (times: number, systemLanguage: General.SystemLanguage) => {
  return systemLanguage === 'en-US' || systemLanguage === 'tl' ? timeEnMap[times] : times;
};

export const useGetTimes = () => {
  const systemLanguage = useSystemLanguageValue();

  return {
    systemLanguage,
    getTimes: useCallback((times: number) => getTimes(times, systemLanguage), [systemLanguage]),
  };
};

function RoundForm(props: RoundFormProps) {
  const {
    disabled = false,
    initialValues,
    children,
    scenarioType,
    task,
    onRoundUpdate,
    ...otherProps
  } = props;

  const firstRoundMinTime = scenarioFirstRoundMinTimeMap[scenarioType];

  const firstRoundMaxTime = scenarioType === 'INVITE_FOLLOWER' ? 360 : 30;

  const { round: roundIndex } = initialValues;

  const { getTimes } = useGetTimes();

  const { t } = useTranslate();

  const {
    orderReminderTitle: { text: orderReminderTitle },
  } = useMixingTokoText();
  const scenarioLabel =
    scenarioType === 'ORDER_REMINDER'
      ? orderReminderTitle
      : (t(scenarioTypeI18nLabelMap[scenarioType]) as string);

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { executeTime, executeTimeUnit } = values;

      if (!executeTime && executeTime !== 0) {
        errors.executeTime = t('marketing.请输入执行时间');
      } else if (roundIndex === 1) {
        if (executeTime > firstRoundMaxTime || executeTime < firstRoundMinTime) {
          errors.executeTime = t('marketing.执行时间不得超出范围x-y timeUnit', {
            x: firstRoundMinTime,
            y: firstRoundMaxTime,
            timeUnit: t('marketing.分钟'),
          });
        }
      } else {
        const min = unitTimeMinMap[executeTimeUnit];
        const max = unitTimeMaxMap[executeTimeUnit];

        const unitLabelMap = {
          '1': t('marketing.分钟'),
          '2': t('marketing.小时'),
        };

        if (executeTime > max || executeTime < min) {
          errors.executeTime = t('marketing.执行时间不得超出范围x-y timeUnit', {
            x: min,
            y: max,
            timeUnit: unitLabelMap[executeTimeUnit],
          });
        }
      }

      return errors;
    },
    [roundIndex, firstRoundMinTime, firstRoundMaxTime, t],
  );

  const [editable, setEditable] = useState(false);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={validate}
      onSubmit={(values, helpers) => {
        onRoundUpdate(values)
          .then(() => {
            message.success(t('operation.saveSucceed'));

            helpers.resetForm();

            setEditable(false);
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
      {...otherProps}
    >
      {(props) => {
        const {
          isSubmitting,
          values: { executeTimeUnit },
          setFieldValue,
          handleSubmit,
          resetForm,
        } = props;

        const form = (
          <Form>
            <FormField<FV['executeTime']> name="executeTime" required label={t('执行时间')}>
              {({ field }) => {
                const time = (
                  <TransComponent
                    content={
                      <InlineItem>
                        <InputNumber
                          {...field}
                          disabled={disabled || !editable}
                          min={
                            roundIndex === 1 ? firstRoundMinTime : unitTimeMinMap[executeTimeUnit]
                          }
                          max={
                            roundIndex === 1 ? firstRoundMaxTime : unitTimeMaxMap[executeTimeUnit]
                          }
                          precision={0}
                        />
                      </InlineItem>
                    }
                  />
                );

                const unit =
                  roundIndex === 1 ? (
                    <TransComponent content={<>{t('marketing.分钟')}</>} />
                  ) : (
                    <TransComponent
                      content={
                        <InlineItem>
                          <Select
                            disabled={disabled || !editable}
                            value={executeTimeUnit}
                            onChange={(value) => setFieldValue('executeTimeUnit', value)}
                            options={[
                              {
                                value: '1',
                                label: t('marketing.分钟'),
                              },
                              {
                                value: '2',
                                label: t('marketing.小时'),
                              },
                            ]}
                            allowClear={false}
                          />
                        </InlineItem>
                      }
                    />
                  );

                const content = (() => {
                  const triggerAction = t(
                    scenarioType === 'PRAISE_REMINDER'
                      ? scenarioTriggerActionI18nMap.PRAISE_REMINDER[
                          task.praiseTriggerPoint || 'CONFIRMED'
                        ]
                      : scenarioTriggerActionI18nMap[scenarioType],
                  );

                  if (roundIndex === 1) {
                    return (
                      <T
                        keyName="marketing.action x timeUnit后"
                        params={{ action: triggerAction, x: time, timeUnit: unit }}
                      />
                    );
                  }

                  if (scenarioType === 'PAYMENT_REMINDER') {
                    return (
                      <T
                        keyName="marketing.第x次action y timeUnit后"
                        params={{
                          x: getTimes(roundIndex - 1),
                          y: time,
                          timeUnit: unit,
                          action: scenarioLabel,
                        }}
                      />
                    );
                  }

                  return (
                    <T
                      keyName="marketing.第x次action1或最后一次action2 y timeUnit后"
                      params={{
                        x: getTimes(roundIndex - 1),
                        action1: scenarioLabel,
                        action2: triggerAction,
                        y: time,
                        timeUnit: unit,
                      }}
                    />
                  );
                })();

                return (
                  <>
                    {content}
                    {editable ? (
                      <>
                        <Button
                          type="link"
                          onClick={() => {
                            setEditable(false);
                            resetForm();
                          }}
                        >
                          {t('marketing.取消')}
                        </Button>
                        <Button type="link" loading={isSubmitting} onClick={() => handleSubmit()}>
                          {t('marketing.保存')}
                        </Button>
                      </>
                    ) : (
                      <Button disabled={disabled} type="link" onClick={() => setEditable(true)}>
                        {t('marketing.编辑')}
                      </Button>
                    )}
                  </>
                );
              }}
            </FormField>
          </Form>
        );

        return form;
      }}
    </Formik>
  );
}

export default RoundForm;
